import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, redirect, useLocation } from '@tanstack/react-router';
import { PlusIcon } from '@phosphor-icons/react';
import {
  TabbedPageLayout,
  type RoutedTab,
} from '@/components/tabbed-page-layout';
import { Button } from '@/components/ui/button';
import { hasPermission } from '@/lib/permissions';
import { RequireOrgEdit } from '@/components/require-permission';
import { companyProfilesOptions } from '@/hooks/query-options';
import { useFeatures } from '@/hooks/use-features';
import { AddTargetAccountDialog } from '@/components/profiles/add-target-account-dialog';
import { ProfileWizard } from '@/components/profiles/profile-wizard';

export const Route = createFileRoute('/_auth/_side-layout/actions/profiles')({
  // Actions › Profiles (Concord GTM Screens 4d): the org's own Company
  // Profiles (the GTM context every agent runs on) and the Target Profiles
  // for the accounts you sell into.
  component: ProfilesLayout,
  beforeLoad: async ({ context }) => {
    if (!hasPermission(context.userRole, { reports: ['read'] })) {
      throw redirect({ to: '/' });
    }
  },
  loader: ({ context: { queryClient } }) =>
    queryClient.ensureQueryData(companyProfilesOptions()),
});

const TARGET_ACCOUNTS_PATH = '/actions/profiles/personas';

const TABS: RoutedTab[] = [
  { to: '/actions/profiles/profiles', label: 'Our Profiles' },
  { to: TARGET_ACCOUNTS_PATH, label: 'Target Accounts' },
];

function ProfilesLayout() {
  const { data } = useQuery(companyProfilesOptions());
  const { pathname } = useLocation();
  const { isEnabled } = useFeatures();
  const [wizardOpen, setWizardOpen] = useState(false);
  const [addAccountOpen, setAddAccountOpen] = useState(false);
  const onTargetAccounts = pathname.startsWith(TARGET_ACCOUNTS_PATH);
  // The header CTA appears once a profile exists. The empty state's inline
  // "Build My Profile" is the first-run entry point.
  const hasProfiles = (data?.profiles ?? []).length > 0;

  return (
    <>
      <TabbedPageLayout
        title="Profiles"
        description="Your company's GTM profile and the target account profiles you're selling into."
        isNew
        tabs={TABS}
        actions={
          onTargetAccounts ? (
            isEnabled('targetAccounts') && (
              <RequireOrgEdit>
                <Button
                  iconLeft={<PlusIcon />}
                  onClick={() => setAddAccountOpen(true)}
                >
                  Add Target Account
                </Button>
              </RequireOrgEdit>
            )
          ) : hasProfiles ? (
            <RequireOrgEdit>
              <Button
                iconLeft={<PlusIcon />}
                onClick={() => setWizardOpen(true)}
              >
                New Profile
              </Button>
            </RequireOrgEdit>
          ) : undefined
        }
      />
      <ProfileWizard open={wizardOpen} onOpenChange={setWizardOpen} />
      <AddTargetAccountDialog
        open={addAccountOpen}
        onOpenChange={setAddAccountOpen}
      />
    </>
  );
}
